import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import CommonSelect from "../../../core/common/commonSelect";
import Table from "../../../core/common/dataTable/index";
import { Link } from "react-router-dom";
import { DatePicker, TimePicker, Input, Button } from "antd";
import {
  getStudentAttendance,
  markStudentAttendance,
  resetStudentAttendance,
  type StudentAttendanceResponse,
} from "../../../service/student-attendance";
import dayjs from "dayjs";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import {
  getAllClasses,
  type Class,
} from "../../../service/classes";
import { listGroups, type Group } from "../../../service/group";
import { Option } from "../../../core/common/multiSelect";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useSelector } from "react-redux";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";
import { selectUser } from "../../../core/data/redux/slice/user";
import { toast } from "react-toastify";
export interface InOutRecord {
  type: "in" | "out";
  time: string;
}

// Local to this screen — the shared CLASSES_SORT_OPTIONS (Newest/Oldest by
// createdAt) doesn't make sense for a roster you sort by name or GR number.
const STUDENT_ATTENDANCE_SORT_OPTIONS = [
  { label: "GR Number", value: { sortBy: "grNumber", sortOrder: "asc" } },
  { label: "Name (A->Z)", value: { sortBy: "name", sortOrder: "asc" } },
  { label: "Name (Z->A)", value: { sortBy: "name", sortOrder: "desc" } },
] as const;

// No pagination on this screen — every student in the selected Class/Section
// (optionally Group) loads at once, well above any realistic roster size.
const ALL_STUDENTS_LIMIT = 2000;

// Same palette as the Attendance Report page, so a status reads the same
// color everywhere in the app — plain text was easy to miss at a glance.
const STATUS_COLORS: Record<string, string> = {
  present: "#10B981",
  absent: "#EF4444",
  late: "#3d5ee1",
  "half-day": "#A855F7",
  leave: "#3B82F6",
  missing: "#64748B",
};
const STATUS_TEXT_COLOR = (status: string) =>
  status === "late" ? "#1a1a1a" : "#fff";

const StudentAttendance = () => {
  const user = useSelector(selectUser);
  const componentRef = useRef<HTMLDivElement>(null);
  const currentSession = useSelector(selectCurrentSession);
  const systems = useSelector(selectSystemsForCurrentBranch);
  const currentBranch = useSelector(selectCurrentBranch);
  // Present-default campuses take manual attendance (most students are
  // present), so the scanner marks the exceptions absent instead. Absent-
  // default campuses (EON, has a machine) keep scanning-in = present.
  const scanMarksAbsent = currentBranch?.studentAttendanceDefault === "present";

  const [attendanceData, setAttendanceData] = useState<
    StudentAttendanceResponse[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [markingLoading, setMarkingLoading] = useState<Record<string, boolean>>(
    {}
  );
  const [classes, setClasses] = useState<Class[]>([]);
  const [classOptions, setClassOptions] = useState<Option[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null
  );

  // Pending selections shown in the toolbar — System -> Class -> Section (in
  // sequence) -> Group (optional). Nothing is fetched until Search is
  // clicked; see `hasSearched` below.
  const [filters, setFilters] = useState({
    system: undefined as string | undefined,
    classId: "",
    sectionId: "",
    groupId: "",
    date: dayjs().format("YYYY-MM-DD"),
  });

  // The last filter set actually searched for — drives fetchAttendanceData.
  const [appliedFilters, setAppliedFilters] = useState({
    classId: "",
    sectionId: "",
    groupId: "",
    date: dayjs().format("YYYY-MM-DD"),
    search: "",
    page: 1,
    limit: ALL_STUDENTS_LIMIT,
    sortBy: STUDENT_ATTENDANCE_SORT_OPTIONS[0].value.sortBy as string,
    sortOrder: STUDENT_ATTENDANCE_SORT_OPTIONS[0].value.sortOrder as "asc" | "desc",
    sessionId: currentSession?._id || "",
    system: undefined as string | undefined,
  });

  // No data is shown until the admin explicitly clicks Search.
  const [hasSearched, setHasSearched] = useState(false);

  // Sections available for the currently selected class only — a class's own
  // `sections` array (already scoped to this campus/session by the backend),
  // not the global section catalog.
  const sectionOptions: Option[] = useMemo(() => {
    const selectedClass = classes.find((c) => c._id === filters.classId);
    return (selectedClass?.sections || []).map((section: any) => ({
      label: section.name,
      value: section._id,
    }));
  }, [classes, filters.classId]);

  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [checkInTimes, setCheckInTimes] = useState<Record<string, string>>({});
  const [checkOutTimes, setCheckOutTimes] = useState<Record<string, string>>(
    {}
  );
  const [searchText, setSearchText] = useState("");

  // Barcode scan-to-mark state — a physical barcode reader types into this
  // field like a keyboard and ends with Enter, no camera needed.
  const [scanValue, setScanValue] = useState("");
  const [scanning, setScanning] = useState(false);
  const [sortBy, setSortBy] = useState<(typeof STUDENT_ATTENDANCE_SORT_OPTIONS)[number]>(
    STUDENT_ATTENDANCE_SORT_OPTIONS[0]
  );

  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const fetchClasses = async () => {
    try {
      setLoadingClasses(true);

      const classesResponse = await getAllClasses({
        limit: 100,
        system: filters.system,
        session: currentSession?._id,
      });
      setClasses(classesResponse.classes);
      const classOpts: Option[] = classesResponse.classes.map((cls: Class) => ({
        label: cls.name,
        value: cls._id,
      }));
      setClassOptions(classOpts);
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error("Failed to fetch classes");
    } finally {
      setLoadingClasses(false);
    }
  };

  // Groups (academic streams, e.g. Computer/Biology) that this class belongs
  // to — only shown as a dropdown when a class actually has any.
  const fetchGroups = async (classId: string) => {
    if (!classId) {
      setGroups([]);
      return;
    }
    try {
      setLoadingGroups(true);
      const res = await listGroups({ system: filters.system, class: classId });
      setGroups(res?.data || []);
    } catch (error) {
      console.error("Error fetching groups:", error);
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchAttendanceData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getStudentAttendance({
        classId: appliedFilters.classId || undefined,
        sectionId: appliedFilters.sectionId || undefined,
        groupId: appliedFilters.groupId || undefined,
        date: appliedFilters.date || undefined,
        search: appliedFilters.search || undefined,
        sortBy: appliedFilters.sortBy,
        sortOrder: appliedFilters.sortOrder,
        page: appliedFilters.page,
        limit: appliedFilters.limit,
        sessionId: appliedFilters.sessionId || currentSession?._id,
        systemId: appliedFilters.system || undefined,
      });

      if (response.success && response.data) {
        setAttendanceData(response.data);
        setPagination(response.pagination);

        const initialRemarks: Record<string, string> = {};
        const initialCheckInTimes: Record<string, string> = {};
        const initialCheckOutTimes: Record<string, string> = {};

        response.data.forEach((item) => {
          if (item.attendance) {
            initialRemarks[item.enrollmentId] = item.attendance.remarks || "";
            const latestIn = item.attendance.inOut
              ?.slice()
              .reverse()
              .find((r: InOutRecord) => r.type === "in");

            if (latestIn) {
              initialCheckInTimes[item.enrollmentId] = dayjs(
                latestIn.time
              ).format("HH:mm");
            }
            const latestOutRecord = item.attendance.inOut
              ?.slice()
              .reverse()
              .find((rec: any) => rec.type === "out");

            if (latestOutRecord) {
              initialCheckOutTimes[item.enrollmentId] = dayjs(
                latestOutRecord.time
              ).format("HH:mm");
            }
          }
        });
        setRemarks(initialRemarks);
        setCheckInTimes(initialCheckInTimes);
        setCheckOutTimes(initialCheckOutTimes);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  }, [
    appliedFilters.classId,
    appliedFilters.sectionId,
    appliedFilters.groupId,
    appliedFilters.date,
    appliedFilters.search,
    appliedFilters.page,
    appliedFilters.limit,
    appliedFilters.sortBy,
    appliedFilters.sortOrder,
    appliedFilters.sessionId,
    appliedFilters.system,
    currentSession?._id,
  ]);

  // The server still answers success:true when it went through skipping a
  // row it refused (a TC'd, struck-off, or left-without-information student
  // has nothing to mark) — surfaced here, not left as a click that silently
  // does nothing, which is exactly the confusion that got this checked in
  // the first place.
  const reportMarkResult = (
    response: { success: boolean; skipped?: { reason: string }[] },
    successMessage: string
  ) => {
    if (response.skipped?.length) {
      response.skipped.forEach((s) => toast.warning(s.reason));
    } else if (response.success) {
      toast.success(successMessage);
    } else {
      toast.error("Failed to mark attendance");
    }
  };

  const handleMarkAttendance = async (enrollmentId: string) => {
    try {
      setMarkingLoading((prev) => ({ ...prev, [enrollmentId]: true }));

      const currentDate = dayjs(appliedFilters.date);

      let checkInTimeISO;
      if (checkInTimes[enrollmentId]) {
        const [hours, minutes] = checkInTimes[enrollmentId].split(":");
        checkInTimeISO = currentDate
          .hour(parseInt(hours))
          .minute(parseInt(minutes))
          .second(0)
          .millisecond(0)
          .toISOString();
      } else {
        checkInTimeISO = dayjs().toISOString();
      }

      let checkOutTimeISO;
      if (checkOutTimes[enrollmentId]) {
        const [hours, minutes] = checkOutTimes[enrollmentId].split(":");
        checkOutTimeISO = currentDate
          .hour(parseInt(hours))
          .minute(parseInt(minutes))
          .second(0)
          .millisecond(0)
          .toISOString();
      } else {
        checkOutTimeISO = undefined;
      }

      const attendanceData = {
        attendances: [
          {
            enrollmentId,
            date: appliedFilters.date,
            in: checkInTimeISO,
            out: checkOutTimeISO,
            remarks: remarks[enrollmentId] || "",
          },
        ],
        markedBy: user._id,
      };
      const response = await markStudentAttendance(attendanceData);
      reportMarkResult(response, "Attendance marked successfully");
      await fetchAttendanceData();
    } catch (error) {
      console.error("Error marking attendance:", error);
      toast.error("Failed to mark attendance");
      await fetchAttendanceData();
    } finally {
      setMarkingLoading((prev) => ({ ...prev, [enrollmentId]: false }));
    }
  };

  const handleMarkAbsent = async (enrollmentId: string) => {
    try {
      setMarkingLoading((prev) => ({ ...prev, [enrollmentId]: true }));

      const response = await markStudentAttendance({
        attendances: [
          {
            enrollmentId,
            date: appliedFilters.date,
            status: "absent",
            remarks: remarks[enrollmentId] || "",
          },
        ],
        markedBy: user._id,
      });

      reportMarkResult(response, "Marked absent");
      await fetchAttendanceData();
    } catch (error) {
      console.error("Error marking absent:", error);
      toast.error("Failed to mark attendance");
    } finally {
      setMarkingLoading((prev) => ({ ...prev, [enrollmentId]: false }));
    }
  };

  const handleMarkLeave = async (enrollmentId: string) => {
    try {
      setMarkingLoading((prev) => ({ ...prev, [enrollmentId]: true }));

      const response = await markStudentAttendance({
        attendances: [
          {
            enrollmentId,
            date: appliedFilters.date,
            status: "leave",
            remarks: remarks[enrollmentId] || "",
          },
        ],
        markedBy: user._id,
      });

      reportMarkResult(response, "Marked on leave");
      await fetchAttendanceData();
    } catch (error) {
      console.error("Error marking leave:", error);
      toast.error("Failed to mark attendance");
    } finally {
      setMarkingLoading((prev) => ({ ...prev, [enrollmentId]: false }));
    }
  };

const handleResetAttendance = async (enrollmentId: string) => {
  try {
    setMarkingLoading((prev) => ({ ...prev, [enrollmentId]: true }));

    const response = await resetStudentAttendance({
      date: appliedFilters.date,
      enrollmentIds: [enrollmentId],
    });

    if (response.success) {
      toast.success(
        `Attendance reset! (${response.deletedCount} record deleted)`
      );
      await fetchAttendanceData();
    } else {
      toast.error(response.message || "Failed to reset");
    }
  } catch (error: any) {
    toast.error(error.response?.data?.message || "Server error");
  } finally {
    setMarkingLoading((prev) => ({ ...prev, [enrollmentId]: false }));
    setSelectedStudentId(null);
  }
};


  const handleRemarksChange = (enrollmentId: string, remark: string) => {
    setRemarks((prev) => ({
      ...prev,
      [enrollmentId]: remark,
    }));
  };

  const handleCheckInTimeChange = (
    enrollmentId: string,
    time: string | null
  ) => {
    if (time) {
      setCheckInTimes((prev) => ({
        ...prev,
        [enrollmentId]: time,
      }));
    }
  };
  const handleCheckOutTimeChange = (
    enrollmentId: string,
    time: string | null
  ) => {
    if (time) {
      setCheckOutTimes((prev) => ({
        ...prev,
        [enrollmentId]: time,
      }));
    }
  };
  // System -> Class -> Section -> Group(optional), each step resets the ones
  // after it so a stale section/group from a previous class can't linger.
  const handleSystemChange = (systemItem: any) => {
    setFilters((prev) => ({
      ...prev,
      system: systemItem?._id,
      classId: "",
      sectionId: "",
      groupId: "",
    }));
    setGroups([]);
  };

  const handleClassFilterChange = (selectedOption: any) => {
    const classId = selectedOption?.value || "";
    setFilters((prev) => ({
      ...prev,
      classId,
      sectionId: "",
      groupId: "",
    }));
    fetchGroups(classId);
  };

  const handleSectionFilterChange = (selectedOption: any) => {
    setFilters((prev) => ({
      ...prev,
      sectionId: selectedOption?.value || "",
    }));
  };

  const handleGroupFilterChange = (selectedOption: any) => {
    setFilters((prev) => ({
      ...prev,
      groupId: selectedOption?.value || "",
    }));
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setFilters((prev) => ({
        ...prev,
        date: date.format("YYYY-MM-DD"),
      }));
    }
  };

  // System, Class and Section are required in sequence; Group is optional.
  const canSearch = Boolean(filters.system && filters.classId && filters.sectionId);

  const handleSearchClick = () => {
    if (!canSearch) return;
    setAppliedFilters((prev) => ({
      ...prev,
      system: filters.system,
      classId: filters.classId,
      sectionId: filters.sectionId,
      groupId: filters.groupId,
      date: filters.date,
      sessionId: currentSession?._id || "",
      page: 1,
    }));
    setHasSearched(true);
  };

  // Only clears Class/Section/Group — System and Date stay as selected, the
  // admin usually wants to pick a different class on the same date/system.
  const handleResetClick = () => {
    setFilters((prev) => ({
      ...prev,
      classId: "",
      sectionId: "",
      groupId: "",
    }));
    setGroups([]);
    setAttendanceData([]);
    setHasSearched(false);
    setSearchText("");
    setSortBy(STUDENT_ATTENDANCE_SORT_OPTIONS[0]);
  };

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  const handleSortSelect = (item: (typeof STUDENT_ATTENDANCE_SORT_OPTIONS)[number]) => {
    setSortBy(item);
    setAppliedFilters((prev) => ({
      ...prev,
      sortBy: item.value.sortBy,
      sortOrder: item.value.sortOrder as "asc" | "desc",
      page: 1,
    }));
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Student Attendance Report",
  });

  const handleExportExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        attendanceData.map((item) => {
          const inRecord = item.attendance?.inOut?.find(
            (rec: any) => rec.type === "in"
          );
          const outRecord = item.attendance?.inOut?.find(
            (rec: any) => rec.type === "out"
          );

          return {
            "GR Number": item.student?.grNumber || "N/A",
            "Student Name": `${item.user?.firstName || ""} ${
              item.user?.lastName || ""
            }`,
            Class: item.class?.name || "N/A",
            Section: item.section?.name || "N/A",
            Attendance: item.status || "Absent",
            "Check-In Time": inRecord
              ? dayjs(inRecord.time).format("HH:mm")
              : "N/A",
            "Check-Out Time": outRecord
              ? dayjs(outRecord.time).format("HH:mm")
              : "N/A",
            Date: item.date ? new Date(item.date).toLocaleDateString() : "N/A",
            Remarks: item.attendance?.remarks || "",
          };
        })
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
      XLSX.writeFile(workbook, "student_attendance.xlsx");
      toast.success("Exported to Excel successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export to Excel");
    }
  };

  // Resolve a scanned GR number to its enrollment (backend search matches
  // student.grNumber) and quick-mark it. In present-default campuses (manual
  // attendance) that means marking the exception ABSENT; in absent-default
  // campuses (EON, has a scanning machine) it means marking PRESENT.
  const handleScan = async (raw?: string) => {
    const gr = String(raw ?? scanValue).trim();
    if (!gr) return;
    setScanning(true);
    try {
      const res = await getStudentAttendance({
        date: filters.date || undefined,
        search: gr,
        page: 1,
        limit: 20,
        sessionId: currentSession?._id,
        systemId: filters.system || undefined,
      });
      const items = res?.success && res.data ? res.data : [];
      const match = items.find(
        (i) => String(i.student?.grNumber || "").trim() === gr
      );
      if (!match) {
        toast.error(`No student found for GR ${gr}`);
        return;
      }
      const name = `${match.user?.firstName || ""} ${match.user?.lastName || ""}`.trim();
      const response = await markStudentAttendance({
        attendances: scanMarksAbsent
          ? [
              {
                enrollmentId: match.enrollmentId,
                date: filters.date,
                status: "absent",
                remarks: "Scanned",
              },
            ]
          : [
              {
                enrollmentId: match.enrollmentId,
                date: filters.date,
                in: dayjs().toISOString(),
                remarks: "Scanned",
              },
            ],
        markedBy: user._id,
      });
      reportMarkResult(
        response,
        `Marked ${name || gr} ${scanMarksAbsent ? "absent" : "present"}`
      );
      setScanValue("");
      if (hasSearched) await fetchAttendanceData();
    } catch (error) {
      console.error("Scan error:", error);
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  // Default to the first available system once systems load, so System
  // already has a value for the sequential Class -> Section flow.
  useEffect(() => {
    if (!filters.system && systems.length) {
      setFilters((prev) => ({ ...prev, system: systems[0]._id }));
    }
  }, [systems, filters.system]);

  useEffect(() => {
    if (filters.system) {
      fetchClasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.system, currentSession?._id]);

  // Nothing is fetched until Search is clicked (hasSearched) — after that,
  // any change that updates appliedFilters (pagination, sort, table search,
  // or another Search click) re-runs this since fetchAttendanceData's
  // identity changes with it.
  useEffect(() => {
    if (hasSearched) fetchAttendanceData();
  }, [fetchAttendanceData, hasSearched]);

  useEffect(() => {
    setSearchText(appliedFilters.search);
  }, [appliedFilters.search]);

  useEffect(() => {
    if (!hasSearched) return;
    const timeoutId = setTimeout(() => {
      setAppliedFilters((prev) => ({
        ...prev,
        search: searchText,
        page: 1,
      }));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchText, hasSearched]);

  const columns = [
    {
      title: "GR No",
      dataIndex: "student",
      render: (student: any) => student?.grNumber || "N/A",
      sorter: (a: any, b: any) => {
        const grA = a.student?.grNumber || "";
        const grB = b.student?.grNumber || "";
        return grA.localeCompare(grB);
      },
    },
    {
      title: "Name",
      dataIndex: "user",
      render: (user: any, record: StudentAttendanceResponse) => (
        <div className="d-flex align-items-center">
          <Link to="#" className="avatar avatar-md">
            <ImageWithBasePath
              src={"assets/img/students/student-01.jpg"}
              fullSrc={user?.profilePicture}
              className="img-fluid rounded-circle"
              alt="img"
            />
          </Link>
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link to="#">
                {`${user?.firstName || ""} ${user?.lastName || ""}`}
              </Link>
            </p>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameA = `${a.user?.firstName || ""} ${
          a.user?.lastName || ""
        }`.toLowerCase();
        const nameB = `${b.user?.firstName || ""} ${
          b.user?.lastName || ""
        }`.toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Class",
      dataIndex: "class",
      render: (classData: any) => classData?.name || "N/A",
      sorter: (a: any, b: any) => {
        const classA = a.class?.name || "";
        const classB = b.class?.name || "";
        return classA.localeCompare(classB);
      },
    },
    {
      title: "Section",
      dataIndex: "section",
      render: (section: any) => section?.name || "N/A",
      sorter: (a: any, b: any) => {
        const sectionA = a.section?.name || "";
        const sectionB = b.section?.name || "";
        return sectionA.localeCompare(sectionB);
      },
    },
    {
      title: "Check-In Time",
      dataIndex: "attendance",
      render: (attendance: any, record: StudentAttendanceResponse) => {
        // Get initial value from existing attendance or user input
        const initialValue = checkInTimes[record.enrollmentId]
          ? dayjs(checkInTimes[record.enrollmentId], "HH:mm")
          : record.attendance?.inOut?.find((rec: any) => rec.type === "in")
              ?.time
          ? dayjs(
              record.attendance.inOut.find((rec: any) => rec.type === "in")
                ?.time
            )
          : null;

        return (
          <TimePicker
            format="HH:mm"
            value={initialValue}
            onChange={(time) =>
              handleCheckInTimeChange(
                record.enrollmentId,
                time ? time.format("HH:mm") : null
              )
            }
            disabled={markingLoading[record.enrollmentId]}
            style={{ width: "100px" }}
            placeholder="HH:mm"
          />
        );
      },
    },
    {
      title: "Check-Out Time",
      dataIndex: "attendance",
      render: (attendance: any, record: StudentAttendanceResponse) => {
        const stateOutTime = checkOutTimes[record.enrollmentId];

        const backendLatestOut = record.attendance?.inOut
          ?.slice()
          .reverse()
          .find((rec: any) => rec.type === "out");

        const displayOutTime =
          stateOutTime ||
          (backendLatestOut
            ? dayjs(backendLatestOut.time).format("HH:mm")
            : null);

        return (
          <TimePicker
            format="HH:mm"
            value={displayOutTime ? dayjs(displayOutTime, "HH:mm") : null}
            onChange={(time) =>
              handleCheckOutTimeChange(
                record.enrollmentId,
                time ? time.format("HH:mm") : null
              )
            }
            disabled={markingLoading[record.enrollmentId]}
            placeholder="HH:mm"
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_: any, record: StudentAttendanceResponse) => {
        const status = record.status || "absent";
        const label = status.charAt(0).toUpperCase() + status.slice(1);
        return (
          <span
            className="badge"
            style={{
              backgroundColor: STATUS_COLORS[status] || STATUS_COLORS.absent,
              color: STATUS_TEXT_COLOR(status),
              fontSize: "12px",
              padding: "5px 10px",
              borderRadius: "12px",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
        );
      },
      sorter: (a: any, b: any) => {
        const statusA = a.status || "";
        const statusB = b.status || "";
        return statusA.localeCompare(statusB);
      },
    },
    {
      title: "Action",
      dataIndex: "attendance",
      // Buttons only, no data — pointless (and, until hidden, blank) on a
      // printed sheet.
      className: "no-print",
      render: (attendance: any, record: StudentAttendanceResponse) => {
        const hasPermission =
          user.permissions?.includes("student-attendance.create") ||
          !user.permissions?.length;
        // Same as the staff sheet: no permission means no buttons, rather than
        // three dead ones whose forced background made the disabled text
        // unreadable.
        if (!hasPermission) {
          return <small className="text-muted">No permission to mark attendance</small>;
        }

        return (
          <div className="d-flex align-items-center flex-wrap gap-1">
            <Button
              type="primary"
              size="small"
              loading={markingLoading[record.enrollmentId]}
              onClick={() => handleMarkAttendance(record.enrollmentId)}
            >
              {markingLoading[record.enrollmentId]
                ? "Marking..."
                : "Mark Attendance"}
            </Button>
            <Button
              danger
              size="small"
              loading={markingLoading[record.enrollmentId]}
              onClick={() => handleMarkAbsent(record.enrollmentId)}
            >
              Mark Absent
            </Button>
            <Button
              size="small"
              style={{ backgroundColor: STATUS_COLORS.leave, color: "#fff", border: "none" }}
              loading={markingLoading[record.enrollmentId]}
              onClick={() => handleMarkLeave(record.enrollmentId)}
            >
              Mark Leave
            </Button>
          </div>
        );
      },
    },
    {
      title: "Reset Attendance",
      dataIndex: "attendance",
      // Button only, no data — pointless (and, until hidden, blank) on a
      // printed sheet.
      className: "no-print",
      render: (attendance: any, record: any) => {
        const hasAttendanceMarked =
          record.attendance && record.attendance.inOut?.length > 0;

        const isDisabled =
          !hasAttendanceMarked ||
          (!user.permissions?.includes("employee-attendance.create") &&
            !!user.permissions?.length);

        return (
          <Button
            type="primary"
            size="small"
            loading={markingLoading[record.employeeId]}
            disabled={isDisabled}
            className={
              isDisabled
                ? "bg-secondary border-secondary text-white opacity-50 cursor-not-allowed"
                : "bg-primary"
            }
            data-bs-toggle="modal"
            data-bs-target="#delete-modal"
            onClick={() => setSelectedStudentId(record.enrollmentId)}
          >
            Reset Attendance
          </Button>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "remarks",
      render: (text: string, record: StudentAttendanceResponse) => (
        <div>
          <Input
            placeholder="Enter remarks"
            value={remarks[record.enrollmentId] || ""}
            onChange={(e) =>
              handleRemarksChange(record.enrollmentId, e.target.value)
            }
            disabled={markingLoading[record.enrollmentId]}
            style={{ minWidth: "150px" }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Student List */}
          <div className="card">
            <div className="card-header pb-3 d-flex align-items-start justify-content-between flex-wrap gap-2">
              <div>
                <h4 className="mb-1">Student Attendance List</h4>
                <p className="text-muted small mb-3">
                  Pick a date, then choose System, Class and Section in order (Group is optional) — the
                  same class can have different sections. Click <strong>Search</strong> to load attendance.
                </p>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-light bg-white"
                  onClick={handlePrint}
                >
                  <i className="ti ti-printer me-1" />
                  Print
                </button>
                <button
                  type="button"
                  className="btn btn-outline-light bg-white"
                  onClick={handleExportExcel}
                >
                  <i className="ti ti-file-type-xls me-1" />
                  Export
                </button>
              </div>

              <div className="row g-3 align-items-end">
                <div className="col-6 col-md-2">
                  <label className="form-label fw-semibold">Date</label>
                  <DatePicker
                    value={filters.date ? dayjs(filters.date) : null}
                    onChange={handleDateChange}
                    format={(value) =>
                      value.isSame(dayjs(), "day") ? "Today" : value.format("DD-MM-YYYY")
                    }
                    allowClear={false}
                    placeholder="Select Date"
                    disabledDate={(current) => {
                      // A Sunday CAN be a real working day (School Opening
                      // proves it for several campuses) -- only block the
                      // future, matching staff-attendance.tsx.
                      return current > dayjs().endOf("day");
                    }}
                    style={{ height: "38px", width: "100%" }}
                  />
                </div>

                <div className="col-6 col-md-2">
                  <label className="form-label fw-semibold">System</label>
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find((item: any) => item._id === filters.system)}
                    getOptionValue={(item) => item._id}
                    getOptionLabel={(item) => item.name}
                    onChange={handleSystemChange}
                    placeholder="Select System"
                  />
                </div>

                <div className="col-6 col-md-2">
                  <label className="form-label fw-semibold">Class</label>
                  <CommonSelect
                    className="select"
                    options={classOptions}
                    value={classOptions.find((opt) => opt.value === filters.classId)}
                    onChange={handleClassFilterChange}
                    placeholder={
                      loadingClasses
                        ? "Loading..."
                        : !filters.system
                          ? "Select System first"
                          : "Select Class"
                    }
                    isDisabled={!filters.system || loadingClasses}
                  />
                </div>

                <div className="col-6 col-md-2">
                  <label className="form-label fw-semibold">Section</label>
                  <CommonSelect
                    className="select"
                    options={sectionOptions}
                    value={sectionOptions.find((opt) => opt.value === filters.sectionId)}
                    onChange={handleSectionFilterChange}
                    placeholder={
                      !filters.classId
                        ? "Select Class first"
                        : sectionOptions.length === 0
                          ? "No sections"
                          : "Select Section"
                    }
                    isDisabled={!filters.classId || sectionOptions.length === 0}
                  />
                </div>

                {groups.length > 0 && (
                  <div className="col-6 col-md-2">
                    <label className="form-label fw-semibold">
                      Group <span className="text-muted fw-normal">(optional)</span>
                    </label>
                    <CommonSelect
                      className="select"
                      options={groups.map((g) => ({ label: g.name, value: g._id }))}
                      value={groups
                        .map((g) => ({ label: g.name, value: g._id }))
                        .find((opt) => opt.value === filters.groupId)}
                      onChange={handleGroupFilterChange}
                      placeholder={loadingGroups ? "Loading..." : "All groups"}
                      isDisabled={loadingGroups}
                    />
                  </div>
                )}

                <div className="col-6 col-md-2">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-primary flex-grow-1"
                      onClick={handleSearchClick}
                      disabled={!canSearch}
                      title={canSearch ? "Search attendance" : "Select System, Class and Section first"}
                    >
                      <i className="ti ti-search me-1" />
                      Search
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleResetClick}
                      title="Clear filters"
                    >
                      <i className="ti ti-refresh" />
                    </button>
                  </div>
                </div>
              </div>

              {!canSearch && (
                <div className="small text-muted mt-2">
                  <i className="ti ti-info-circle me-1" />
                  Select System, then Class, then Section (in that order) to search attendance.
                </div>
              )}

              {hasSearched && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">
                    {loading ? "Loading…" : `${pagination.total} student${pagination.total === 1 ? "" : "s"} found`}
                  </span>
                  <div className="dropdown">
                    <Link
                      to="#"
                      className="btn btn-outline-light bg-white dropdown-toggle btn-sm"
                      data-bs-toggle="dropdown"
                    >
                      <i className="ti ti-sort-ascending-2 me-2" />
                      Sort by {sortBy.label}
                    </Link>
                    <ul className="dropdown-menu p-3">
                      {STUDENT_ATTENDANCE_SORT_OPTIONS.map((item, index) => (
                        <li key={index}>
                          <div
                            className={`dropdown-item rounded-1 ${
                              item.label === sortBy.label ? "active" : ""
                            }`}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleSortSelect(item)}
                          >
                            {item.label}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Barcode scan-to-mark bar (excluded from print) */}
            <div className="d-flex align-items-center flex-wrap gap-2 px-3 py-2 border-top no-print">
              <span className="fw-semibold me-1">
                <i className="ti ti-barcode me-1" />
                Scan to mark {scanMarksAbsent ? "absent" : "present"}:
              </span>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 260 }}
                placeholder="Scan or type GR number, then Enter"
                value={scanValue}
                onChange={(e) => setScanValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleScan();
                  }
                }}
                disabled={scanning}
                autoFocus
              />
              <button
                type="button"
                className={scanMarksAbsent ? "btn btn-danger btn-sm" : "btn btn-primary btn-sm"}
                onClick={() => handleScan()}
                disabled={scanning || !scanValue.trim()}
              >
                {scanning ? "Marking…" : scanMarksAbsent ? "Mark Absent" : "Mark Present"}
              </button>
            </div>

            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Student Attendance</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              {/* Student List */}
              {hasSearched ? (
                <Table
                  dataSource={attendanceData}
                  columns={columns}
                  loading={loading}
                  setSearchText={handleSearch}
                  hidePagination
                />
              ) : (
                <div className="text-center py-5 text-muted no-print">
                  <i className="ti ti-list-search" style={{ fontSize: 36 }} />
                  <p className="mt-2 mb-0">
                    Select System, Class and Section above, then click <strong>Search</strong> to view attendance.
                  </p>
                </div>
              )}
              {/* /Student List */}
            </div>
          </div>
          {/* /Student List */}
        </div>
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form>
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    Are you sure you want to delete this driver? This action
                    cannot be undone.
                  </p>
                  <div className="d-flex justify-content-center">
                    <Link
                      to="#"
                      className="btn btn-light me-3"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      className="btn btn-danger"
                      data-bs-dismiss="modal"
                      onClick={() => {
                        if (selectedStudentId) {
                          handleResetAttendance(selectedStudentId); // now matches enrollmentId param
                        }
                      }}
                    >
                      Yes, Reset
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAttendance;
